'use client';

import {
  type ComponentProps,
  type ReactNode,
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
  useSyncExternalStore,
} from 'react';

import { X } from 'lucide-react';

import { cn } from '../lib/utils';
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogTitle,
  DialogTrigger,
} from '../shadcn/dialog';
import {
  Popover,
  PopoverClose,
  PopoverContent,
  PopoverTrigger,
} from '../shadcn/popover';
import {
  Sheet,
  SheetClose,
  SheetContent,
  SheetDescription,
  SheetTitle,
  SheetTrigger,
} from '../shadcn/sheet';

// ---------------------------------------------------------------------------
// Hooks
// ---------------------------------------------------------------------------

const PHONE_QUERY = '(max-width: 639px)';

function subscribePhone(onChange: () => void) {
  const mql = window.matchMedia(PHONE_QUERY);
  mql.addEventListener('change', onChange);
  return () => mql.removeEventListener('change', onChange);
}

/**
 * True below the `sm` breakpoint. `undefined` on the server and during
 * hydration, so callers can avoid rendering the wrong variant first.
 */
export function useIsPhone(): boolean | undefined {
  return useSyncExternalStore(
    subscribePhone,
    () => window.matchMedia(PHONE_QUERY).matches,
    () => undefined,
  );
}

export interface VisualViewportState {
  /** Visible height (shrinks when the on-screen keyboard opens). */
  height: number;
  offsetTop: number;
  /** Height of the part of the layout viewport hidden by the keyboard. */
  keyboardInset: number;
}

function readVisualViewport(): VisualViewportState | null {
  const vv = window.visualViewport;
  if (!vv) return null;

  return {
    height: vv.height,
    offsetTop: vv.offsetTop,
    keyboardInset: Math.max(0, window.innerHeight - vv.height - vv.offsetTop),
  };
}

let cachedViewport: VisualViewportState | null = null;

function getViewportSnapshot() {
  const next = readVisualViewport();
  // useSyncExternalStore needs a stable reference while nothing changed
  if (
    next?.height !== cachedViewport?.height ||
    next?.offsetTop !== cachedViewport?.offsetTop ||
    next?.keyboardInset !== cachedViewport?.keyboardInset
  ) {
    cachedViewport = next;
  }
  return cachedViewport;
}

function subscribeViewport(onChange: () => void) {
  const vv = window.visualViewport;
  if (!vv) return () => undefined;

  vv.addEventListener('resize', onChange);
  vv.addEventListener('scroll', onChange);
  return () => {
    vv.removeEventListener('resize', onChange);
    vv.removeEventListener('scroll', onChange);
  };
}

const noopSubscribe = () => () => undefined;
const nullSnapshot = () => null;

/**
 * Tracks `window.visualViewport` so fixed UI can sit above the on-screen
 * keyboard. Returns null without the API, on the server, or when disabled.
 */
export function useVisualViewport(enabled = true) {
  return useSyncExternalStore(
    enabled ? subscribeViewport : noopSubscribe,
    enabled ? getViewportSnapshot : nullSnapshot,
    nullSnapshot,
  );
}

// ---------------------------------------------------------------------------
// Shared pieces
// ---------------------------------------------------------------------------

type PopupMode = 'sheet' | 'popover' | 'dialog';

interface ResponsivePopupContextValue {
  mode: PopupMode;
  dismissible: boolean;
  close: () => void;
}

const ResponsivePopupContext =
  createContext<ResponsivePopupContextValue | null>(null);

/** `mode` and `close()` of the enclosing popup, or null outside of one. */
export function useResponsivePopup() {
  return useContext(ResponsivePopupContext);
}

function usePopupContext(component: string) {
  const ctx = useContext(ResponsivePopupContext);
  if (!ctx) {
    throw new Error(`${component} must be used within <ResponsivePopup>`);
  }
  return ctx;
}

/** Neubrutalist 24px ✕ button shared by every sheet. */
export function PopupCloseIcon({
  className,
  ...props
}: ComponentProps<'button'>) {
  return (
    <button
      type="button"
      aria-label="Zamknij"
      className={cn(
        'inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-md border-2 border-zinc-300 bg-white text-zinc-600 shadow-[2px_2px_0_0_#d4d4d8] transition-all hover:translate-x-[1px] hover:translate-y-[1px] hover:text-zinc-900 hover:shadow-[1px_1px_0_0_#d4d4d8] active:translate-x-[2px] active:translate-y-[2px] active:shadow-none dark:border-zinc-600 dark:bg-zinc-900 dark:text-zinc-300 dark:shadow-[2px_2px_0_0_#52525b] dark:hover:text-zinc-100 dark:hover:shadow-[1px_1px_0_0_#52525b]',
        className,
      )}
      {...props}
    >
      <X className="h-3.5 w-3.5" strokeWidth={2.5} aria-hidden />
    </button>
  );
}

/**
 * Focus the sheet/dialog itself: opening never pops the keyboard or rings
 * the ✕ with a focus outline.
 */
function focusContainer(e: Event) {
  e.preventDefault();
  (e.currentTarget as HTMLElement | null)?.focus({ preventScroll: true });
}

const preventDefault = (e: Event) => e.preventDefault();

// ---------------------------------------------------------------------------
// ResponsivePopup
// ---------------------------------------------------------------------------

interface ResponsivePopupProps {
  children: ReactNode;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  defaultOpen?: boolean;
  /** What to render from the `sm` breakpoint up. Phones always get a sheet. */
  desktop: 'popover' | 'dialog';
  /** When false: no ✕, and outside clicks / Escape don't close it. */
  dismissible?: boolean;
}

/**
 * Pop-up that is a bottom sheet on phones and a popover or dialog elsewhere.
 * Anchored pop-ups taller than a phone screen flip sides and jitter while
 * the page scrolls; a sheet fixed to the viewport doesn't.
 */
export function ResponsivePopup({
  children,
  open,
  onOpenChange,
  defaultOpen = false,
  desktop,
  dismissible = true,
}: ResponsivePopupProps) {
  const isPhone = useIsPhone();
  const [uncontrolledOpen, setUncontrolledOpen] = useState(defaultOpen);
  const isOpen = open ?? uncontrolledOpen;

  const setOpen = useCallback(
    (next: boolean) => {
      if (open === undefined) setUncontrolledOpen(next);
      onOpenChange?.(next);
    },
    [open, onOpenChange],
  );

  const mode: PopupMode = isPhone ? 'sheet' : desktop;

  const value = useMemo(
    () => ({ mode, dismissible, close: () => setOpen(false) }),
    [mode, dismissible, setOpen],
  );

  // Open on mount (e.g. the profile wizard): wait until we know which
  // variant to show instead of flashing the dialog before the sheet.
  if (isPhone === undefined && isOpen) return null;

  const Root = mode === 'sheet' ? Sheet : mode === 'popover' ? Popover : Dialog;

  return (
    <ResponsivePopupContext.Provider value={value}>
      <Root open={isOpen} onOpenChange={setOpen}>
        {children}
      </Root>
    </ResponsivePopupContext.Provider>
  );
}

export function ResponsivePopupTrigger(
  props: ComponentProps<typeof PopoverTrigger>,
) {
  const { mode } = usePopupContext('ResponsivePopupTrigger');
  const Trigger =
    mode === 'sheet'
      ? SheetTrigger
      : mode === 'popover'
        ? PopoverTrigger
        : DialogTrigger;

  return <Trigger {...props} />;
}

export function ResponsivePopupClose(
  props: ComponentProps<typeof PopoverClose>,
) {
  const { mode } = usePopupContext('ResponsivePopupClose');
  const Close =
    mode === 'sheet'
      ? SheetClose
      : mode === 'popover'
        ? PopoverClose
        : DialogClose;

  return <Close {...props} />;
}

type PopoverPositionProps = Pick<
  ComponentProps<typeof PopoverContent>,
  'side' | 'align' | 'sideOffset' | 'collisionPadding' | 'onInteractOutside'
>;

interface ResponsivePopupContentProps {
  children: ReactNode;
  title?: ReactNode;
  /** Visually hidden title for screen readers when there is no `title`. */
  srTitle?: string;
  description?: ReactNode;
  titleClassName?: string;
  descriptionClassName?: string;
  /** Hide the ✕ even when the popup is dismissible. */
  hideClose?: boolean;
  /** Pinned below the scrolling body in a sheet; appended on desktop. */
  footer?: ReactNode;
  /** Applied to the content container in every mode. */
  className?: string;
  sheetClassName?: string;
  desktopClassName?: string;
  popoverProps?: PopoverPositionProps;
  overlayClassName?: string;
}

export function ResponsivePopupContent({
  children,
  title,
  srTitle,
  description,
  titleClassName,
  descriptionClassName,
  hideClose = false,
  footer,
  className,
  sheetClassName,
  desktopClassName,
  popoverProps,
  overlayClassName,
}: ResponsivePopupContentProps) {
  const { mode, dismissible } = usePopupContext('ResponsivePopupContent');
  const viewport = useVisualViewport(mode === 'sheet');
  const showClose = dismissible && !hideClose;
  const hasHeading = !!(title || description);

  const Title =
    mode === 'sheet' ? SheetTitle : mode === 'dialog' ? DialogTitle : 'p';
  const Description =
    mode === 'sheet'
      ? SheetDescription
      : mode === 'dialog'
        ? DialogDescription
        : 'p';

  const closeButton = showClose ? (
    <ResponsivePopupClose asChild>
      <PopupCloseIcon
        data-test="responsive-popup-close"
        className={title ? '-mt-0.5' : 'absolute top-4 right-4 z-10'}
      />
    </ResponsivePopupClose>
  ) : null;

  const header = (
    <>
      {title ? (
        <div className="flex items-start justify-between gap-3">
          <Title className={titleClassName}>{title}</Title>
          {closeButton}
        </div>
      ) : (
        <>
          {srTitle && mode !== 'popover' && (
            <Title className="sr-only">{srTitle}</Title>
          )}
          {closeButton}
        </>
      )}
      {description && (
        <Description className={cn('mt-1.5', descriptionClassName)}>
          {description}
        </Description>
      )}
    </>
  );

  // Radix warns when a dialog has no description unless it's opted out
  const describedBy = description ? {} : { 'aria-describedby': undefined };

  const dismissHandlers = dismissible
    ? {}
    : {
        onPointerDownOutside: preventDefault,
        onInteractOutside: preventDefault,
        onEscapeKeyDown: preventDefault,
      };

  if (mode === 'sheet') {
    const keyboardOpen = !!viewport && viewport.keyboardInset > 0;

    return (
      <SheetContent
        side="bottom"
        showCloseButton={false}
        overlayClassName={cn('bg-black/40', overlayClassName)}
        onOpenAutoFocus={focusContainer}
        {...describedBy}
        {...dismissHandlers}
        className={cn(
          'flex max-h-[85dvh] flex-col gap-0 rounded-t-xl border-t-2 border-zinc-200 p-0 pb-[env(safe-area-inset-bottom)] outline-none dark:border-zinc-700',
          className,
          sheetClassName,
        )}
        style={
          keyboardOpen
            ? {
                bottom: viewport.keyboardInset,
                maxHeight: viewport.height - 24,
                paddingBottom: 0,
              }
            : undefined
        }
      >
        {hasHeading ? (
          <div className="shrink-0 px-5 pt-5 pb-3">{header}</div>
        ) : (
          header
        )}
        <div
          className={cn(
            'min-h-0 flex-1 overflow-y-auto overscroll-contain px-5 pb-5',
            !hasHeading && 'pt-5',
          )}
        >
          {children}
        </div>
        {footer && (
          <div className="shrink-0 border-t border-zinc-200 px-5 py-3 dark:border-zinc-700">
            {footer}
          </div>
        )}
      </SheetContent>
    );
  }

  if (mode === 'popover') {
    return (
      <PopoverContent
        {...popoverProps}
        {...dismissHandlers}
        aria-label={typeof title === 'string' ? title : srTitle}
        className={cn('relative', className, desktopClassName)}
      >
        {hasHeading ? <div className="mb-3">{header}</div> : header}
        {children}
        {footer}
      </PopoverContent>
    );
  }

  return (
    <DialogContent
      hideCloseButton
      overlayClassName={overlayClassName}
      {...describedBy}
      {...dismissHandlers}
      onOpenAutoFocus={focusContainer}
      className={cn('outline-none', className, desktopClassName)}
    >
      {hasHeading ? <div>{header}</div> : header}
      {children}
      {footer}
    </DialogContent>
  );
}
